import type { ErrorInfo, FC, ReactNode, RefObject } from 'react';
import { Component, lazy, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Alert } from 'src/components/feedback/alert';
import { isNetworkError, isServerError } from 'src/hooks/use-axios';
import type { AuthenticatedMonolithUserContextValue } from 'src/hooks/use-monolith-user';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { useReauthenticate } from 'src/hooks/use-reauthenticate';
import { useTitle } from 'src/hooks/use-title';
import {
  Forbidden,
  Gone,
  InvalidQueryParams,
  MissingMock,
  NotFound,
  Unauthorized,
} from 'src/utils/errors';
import { handleError, unknownMonolithError } from 'src/utils/handle-error';
import { isConstraintViolation } from 'src/utils/is-constraint-violation';
import { isObject } from 'src/utils/is-object';
import { isSubscriptionRequired } from 'src/utils/is-subscription-required';
import { isVerificationProblemWithoutField } from 'src/utils/is-verification-problem-without-field';
import { reportToSentry } from 'src/utils/report-to-sentry';

const UnknownError = lazy(() => import('../../pages/errors/unknown'));
const UnhandledConstraintViolation = lazy(
  () => import('../../pages/errors/constraint-violation')
);
const NotFoundPage = lazy(() => import('../../pages/errors/not-found'));
const GonePage = lazy(() => import('../../pages/errors/gone'));
const Network = lazy(() => import('../../pages/errors/network'));
const CorruptedBrowserCache = lazy(
  () => import('../../pages/errors/corrupted-browser-cache')
);

type State = {
  // container is used to handle `throw undefined` easily
  errorContainer?: { error: unknown };
};

export const Title: FC<{ children: string }> = ({ children }) => {
  useTitle(children);
  return null;
};

const SaveErrorLocation: FC<{
  errorPathnameRef: RefObject<string | null>;
}> = ({ errorPathnameRef }) => {
  const { pathname } = useLocation();

  // Record the pathname that produced this error without updating the parent
  // in this commit. The next location change can then remount the boundary.
  errorPathnameRef.current ??= pathname;

  return null;
};

const RedirectToLogin: FC = () => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      to={{
        pathname: '/login',
        search: new URLSearchParams({
          redirectUrl: `${pathname}${search}${hash}`,
          automaticRedirect: 'true',
        }).toString(),
      }}
      replace
    />
  );
};

type RealErrorBoundaryProps = {
  children?: ReactNode;
  errorPathnameRef: RefObject<string | null>;
  setReauthenticate: (value: boolean) => void;
  auth: AuthenticatedMonolithUserContextValue;
};

class RealErrorBoundary extends Component<RealErrorBoundaryProps, State> {
  override state: State = { errorContainer: undefined };

  static getDerivedStateFromError(error: unknown) {
    if (error instanceof MissingMock) throw error;
    return { errorContainer: { error } } satisfies State;
  }

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    const [_, report] = handleError(error, this.props.setReauthenticate);
    if (report) reportToSentry(error, errorInfo);
  }

  override render() {
    const { errorContainer } = this.state;
    const { errorPathnameRef } = this.props;

    if (errorContainer === undefined) return this.props.children;

    const { error } = errorContainer;
    if (isConstraintViolation(error)) {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <UnhandledConstraintViolation error={error} />
        </>
      );
    } else if (isNetworkError(error)) {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <Network />
        </>
      );
    } else if (isServerError(error, 401) || error instanceof Unauthorized) {
      if (!this.props.auth) {
        return (
          <>
            <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
            <RedirectToLogin />
          </>
        );
      }
      return (
        <>
          <Title>Unauthorized</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <p data-testid="error-boundary-unauthorized-message">
            <strong>401</strong>: Your session has expired. Please login again.
          </p>
        </>
      );
    } else if (
      (isServerError(error, 400) || isServerError(error, 500)) &&
      error.config.url?.startsWith(PRISMA_CONFIG.monolithApiUrl) &&
      ((typeof (error.response.data as any).message === 'string' &&
        (error.response.data as any).message) || // sometimes the message is an empty string, so check description as well
        (typeof (error.response.data as any).description === 'string' &&
          (error.response.data as any).description))
    ) {
      // the old backend sometimes responds with human readable error messages
      // e.g. https://sentry.io/organizations/prisma-european-capacity-platf/issues/2397095793/?project=1832012
      const msg: string =
        (error.response.data as any).message ||
        (error.response.data as any).description;
      return (
        <>
          <Title>Error</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          {unknownMonolithError(msg) ? (
            <UnknownError error={error} />
          ) : (
            <Alert type="error">{msg}</Alert>
          )}
        </>
      );
    } else if (isVerificationProblemWithoutField(error)) {
      return (
        <>
          <Title>Error</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <Alert type="error">{error.response.data.message}</Alert>
        </>
      );
    } else if (error instanceof InvalidQueryParams) {
      return (
        <>
          <Title>Error</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <Alert type="error">
            <>
              The URL contains an invalid query parameter ({error.name}=
              {error.value}). Please try to reach this page in a different way.
            </>
          </Alert>
        </>
      );
    } else if (isServerError(error, 403) || error instanceof Forbidden) {
      return (
        <>
          <Title>Forbidden</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <Alert type="warning">
            You don’t have permission to access this page.
          </Alert>
        </>
      );
    } else if (isServerError(error, 404) || error instanceof NotFound) {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <NotFoundPage />
        </>
      );
    } else if (isServerError(error, 410) || error instanceof Gone) {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <GonePage />
        </>
      );
    } else if (isSubscriptionRequired(error)) {
      return (
        <>
          <Title>Subscription Required</Title>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <Alert type="warning">{error.response.data.detail}</Alert>
        </>
      );
    } else if (
      isObject(error) &&
      (error as any).name === 'NS_ERROR_FILE_CORRUPTED'
    ) {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <CorruptedBrowserCache />
        </>
      );
    } else {
      return (
        <>
          <SaveErrorLocation errorPathnameRef={errorPathnameRef} />
          <UnknownError error={error} />
        </>
      );
    }
  }
}

/**
 * This component takes care of showing an appropriate default error message to the user
 * for common known and unknown error cases.
 */
export const ErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  const [key, setKey] = useState(0);
  const { pathname } = useLocation();
  const errorPathnameRef = useRef<string | null>(null);
  const [errorPathname, setErrorPathname] = useState<string | null>(null);
  const [_, setReauthenticate] = useReauthenticate();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  // Copy the ref into state so the reset below is replayed in Strict Mode.
  // Comparing the ref directly drops the setKey on the second render.
  if (errorPathname !== errorPathnameRef.current) {
    setErrorPathname(errorPathnameRef.current);
  }

  // Remount once the location moves away from the page that failed. Updating
  // the key during this render applies it before the children commit.
  if (errorPathname !== null && errorPathname !== pathname) {
    errorPathnameRef.current = null;
    setErrorPathname(null);
    setKey((currentKey) => currentKey + 1);
  }

  return (
    <RealErrorBoundary
      key={key}
      errorPathnameRef={errorPathnameRef}
      setReauthenticate={setReauthenticate}
      auth={monolithUser}
    >
      {children}
    </RealErrorBoundary>
  );
};
